import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Palette (Style B Sunset) ──────────────────────────────────────────────
const PALETTES = {
  B: { key: 'B', primary: '#FF5C5C', primaryShadow: '#B83838', accent: '#FFD23F', accent2: '#3DC9B5', coral: '#FF5C5C', plum: '#5B2E5E', aqua: '#3DC9B5', ink: '#2A1F3D', cream: '#FFF4E6', sand: '#FFE4C4', mutedText: '#7A6B85', heroStart: '#FFB86B', heroEnd: '#FF5C5C' },
  A: { key: 'A', primary: '#2A6B30', primaryShadow: '#1E5C2E', accent: '#E8A020', accent2: '#67A844', coral: '#2A6B30', plum: '#1E5C2E', aqua: '#67A844', ink: '#3E2B1E', cream: '#F8F6F0', sand: '#F0EAD6', mutedText: '#A67C52', heroStart: '#EAF2EA', heroEnd: '#1E5C2E' },
  C: { key: 'C', primary: '#5B4EC7', primaryShadow: '#3B2F9E', accent: '#FFD23F', accent2: '#16A085', coral: '#5B4EC7', plum: '#3B2F9E', aqua: '#16A085', ink: '#1A1A2E', cream: '#F5F3FF', sand: '#EEE9FF', mutedText: '#6A6A80', heroStart: '#B8A9FF', heroEnd: '#5B4EC7' },
};
const FONT = '"Nunito", "Baloo 2", sans-serif';
const Owl = ({ size = 30 }) => <div style={{ fontSize: size * 0.85, lineHeight: 1 }}>🦉</div>;

// Default emoji fallback per topic name keyword → emoji
const TOPIC_EMOJI = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('animal')) return '🐘';
  if (n.includes('food') || n.includes('drink')) return '🍕';
  if (n.includes('cloth')) return '👕';
  if (n.includes('weather')) return '🌤️';
  if (n.includes('home') || n.includes('house')) return '🏠';
  if (n.includes('school')) return '🎒';
  if (n.includes('family') || n.includes('people')) return '👨‍👩‍👧';
  if (n.includes('color')) return '🎨';
  if (n.includes('sport') || n.includes('leisure')) return '⚽';
  if (n.includes('music')) return '🎵';
  if (n.includes('verb')) return '🏃';
  if (n.includes('adjective')) return '💎';
  if (n.includes('body')) return '💪';
  if (n.includes('transport')) return '🚗';
  if (n.includes('nature')) return '🌳';
  return '📚';
};

const MIN = 2;
const MAX = 4;

const PlacementTestTopics = () => {
  const navigate = useNavigate();
  const P = PALETTES.B;

  const [topics, setTopics] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/client/placement-test/topics`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (!cancelled) {
          if (!res.ok) {
            setError(data?.detail || `HTTP ${res.status}`);
          } else {
            setTopics(Array.isArray(data) ? data : []);
          }
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const toggle = (tagId) => {
    const next = new Set(selected);
    if (next.has(tagId)) {
      next.delete(tagId);
    } else {
      if (next.size >= MAX) return;
      next.add(tagId);
    }
    setSelected(next);
  };

  const handleStart = async () => {
    if (selected.size < MIN) return;
    setStarting(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/client/placement-test/start`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ topic_tag_ids: Array.from(selected) }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setError(data?.detail || `HTTP ${res.status}`);
        setStarting(false);
        return;
      }
      sessionStorage.setItem('placement_session', JSON.stringify(data));
      navigate('/placement-test');
    } catch (err) {
      setError(err.message);
      setStarting(false);
    }
  };

  const canStart = selected.size >= MIN && !starting;

  // ─── Top bar ───────────────────────────────────────────────────
  const TopBar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 28px', background: '#fff', borderBottom: `3px solid ${P.ink}`, flexWrap: 'wrap' }}>
      <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fff', border: `2.5px solid ${P.ink}`, boxShadow: `0 3px 0 ${P.ink}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Owl size={30} />
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: P.ink }}>HuPath</div>
      <div style={{ width: 1, height: 22, background: P.ink, opacity: 0.2 }} />
      <div style={{ background: P.accent, color: P.ink, border: `2px solid ${P.ink}`, borderRadius: 8, padding: '4px 10px', fontSize: 10, fontWeight: 800, letterSpacing: 1.5 }}>🎒 HỌC VIÊN MỚI</div>
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 6 }}>
        {[0, 1, 2, 3].map(i => <div key={i} style={{ width: i < 2 ? 32 : 20, height: 8, borderRadius: 4, background: i < 2 ? P.coral : (i === 2 ? P.plum : P.cream), border: `2px solid ${P.ink}` }} />)}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: P.ink, opacity: 0.6 }}>Bước 3/4</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: P.cream, fontFamily: FONT, color: P.ink, display: 'flex', flexDirection: 'column' }}>
      {TopBar}

      <div style={{ flex: 1, padding: '40px 32px 60px', maxWidth: 1100, margin: '0 auto', width: '100%' }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: P.plum, letterSpacing: 3 }}>BƯỚC 3 · CHỌN CHỦ ĐỀ</div>
        <div style={{ fontSize: 36, fontWeight: 800, letterSpacing: -1, marginTop: 6, lineHeight: 1.1 }}>
          Bé thích chủ đề nào? <span style={{ color: P.coral }}>🎯</span>
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, opacity: 0.75, marginTop: 8 }}>
          Chọn <b style={{ background: P.accent, padding: '0 6px', borderRadius: 4 }}>{MIN}-{MAX} chủ đề</b> để Hu chuẩn bị bài kiểm tra phù hợp với bé.
        </div>

        {/* Selection indicator */}
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            background: selected.size >= MIN ? P.aqua : '#fff',
            border: `2.5px solid ${P.ink}`, borderRadius: 10,
            padding: '6px 14px', boxShadow: `0 3px 0 ${P.ink}`,
            fontSize: 13, fontWeight: 800,
            color: selected.size >= MIN ? '#fff' : P.ink,
          }}>
            Đã chọn: {selected.size} / {MAX}
          </div>
          {selected.size < MIN && (
            <div style={{ fontSize: 12, fontWeight: 700, color: P.mutedText }}>
              Chọn thêm {MIN - selected.size} chủ đề nữa
            </div>
          )}
        </div>

        {/* Topic grid */}
        {loading && (
          <div style={{ marginTop: 40, textAlign: 'center', fontSize: 16, fontWeight: 700, color: P.mutedText }}>
            Đang tải chủ đề...
          </div>
        )}
        {error && !loading && (
          <div style={{ marginTop: 40, padding: 20, background: '#FEE', border: `2px solid ${P.coral}`, borderRadius: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: P.coral }}>Có lỗi xảy ra:</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: P.ink, marginTop: 6 }}>{error}</div>
          </div>
        )}

        {!loading && !error && (
          <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
            {topics.map(t => {
              const isSelected = selected.has(t.tag_id);
              const disabled = !isSelected && selected.size >= MAX;
              const emoji = TOPIC_EMOJI(t.name);
              return (
                <div
                  key={t.tag_id}
                  onClick={() => !disabled && toggle(t.tag_id)}
                  style={{
                    background: isSelected ? P.accent : '#fff',
                    border: `3px solid ${P.ink}`,
                    borderRadius: 16,
                    padding: 14,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    boxShadow: isSelected ? `0 6px 0 ${P.plum}` : `0 4px 0 ${P.ink}`,
                    transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                    transition: 'all 0.15s',
                    opacity: disabled ? 0.4 : 1,
                    position: 'relative',
                    textAlign: 'center',
                  }}
                >
                  {isSelected && (
                    <div style={{
                      position: 'absolute', top: -8, right: -8,
                      background: P.aqua, color: '#fff',
                      border: `2.5px solid ${P.ink}`, borderRadius: '50%',
                      width: 28, height: 28,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 800,
                      boxShadow: `0 2px 0 ${P.ink}`,
                    }}>✓</div>
                  )}
                  <div style={{
                    width: 64, height: 64, margin: '0 auto',
                    borderRadius: 16,
                    background: isSelected ? '#fff' : P.cream,
                    border: `2px solid ${P.ink}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 36,
                  }}>{emoji}</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: P.ink, marginTop: 10 }}>{t.name}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: P.plum, marginTop: 2 }}>{t.name_vi}</div>
                  <div style={{
                    marginTop: 8, display: 'inline-block',
                    background: isSelected ? '#fff' : P.cream,
                    border: `1.5px solid ${P.ink}`, borderRadius: 6,
                    padding: '2px 8px',
                    fontSize: 10, fontWeight: 800, color: P.ink,
                  }}>{t.vocab_count} từ</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Sticky bottom CTA */}
        <div style={{
          position: 'sticky', bottom: 20, marginTop: 32,
          display: 'flex', justifyContent: 'center', gap: 12,
        }}>
          <button
            onClick={() => navigate('/placement-test-intro')}
            style={{
              padding: '14px 22px', border: `3px solid ${P.ink}`,
              background: '#fff', color: P.ink,
              fontFamily: FONT, fontSize: 13, fontWeight: 800,
              borderRadius: 14, boxShadow: `0 5px 0 ${P.ink}`, cursor: 'pointer',
            }}
          >← Quay lại</button>
          <button
            onClick={handleStart}
            disabled={!canStart}
            style={{
              flex: 1, maxWidth: 360, padding: '16px',
              border: `3px solid ${P.ink}`,
              background: canStart ? P.coral : '#CCC',
              color: '#fff',
              fontFamily: FONT, fontSize: 16, fontWeight: 800,
              borderRadius: 14,
              boxShadow: canStart ? `0 5px 0 ${P.primaryShadow}` : `0 5px 0 #888`,
              cursor: canStart ? 'pointer' : 'not-allowed',
            }}
          >
            {starting ? 'Đang chuẩn bị...' : `🚀 Bắt đầu (${selected.size}/${MAX})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlacementTestTopics;
